import { SparklesIcon } from "@heroicons/react/outline";
import { useContext, useState } from "react";

import { PlaygroundStoreContext } from "../Playground/playground-store";

import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Textarea } from "./textarea";

import { useAIJSONSchemaAssistant } from "@/queries/playground/useAIJSONSchemaAssistant";
import { IS_AI_FEATURES_ENABLED } from "@/constants";
import { JSONSchema } from "@/types";

interface Props {
  provider?: string;
  onSuccess?: (data: JSONSchema) => void;
  messages?: string[];
  popoverPortalContainer?: HTMLElement | null;
}

const AIJSONSchemaAssistant = ({
  provider,
  onSuccess,
  messages: messagesProp,
  popoverPortalContainer,
}: Props) => {
  const [open, setOpen] = useState(false);
  const mutation = useAIJSONSchemaAssistant();

  const playgroundStore = useContext(PlaygroundStoreContext);
  const playgroundMessages = playgroundStore?.messages.map(m => m.content);

  const [prompt, setPrompt] = useState("");

  if (!IS_AI_FEATURES_ENABLED) {
    return null;
  }

  const handleGenerate = () => {
    const messages = messagesProp ?? playgroundMessages ?? [];

    mutation.mutate(
      { provider, prompt, messages },
      {
        onSuccess: data => {
          onSuccess?.(data);
          setOpen(false);
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="tinyIcon">
          <SparklesIcon className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent portalContainer={popoverPortalContainer} className="w-96 p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <SparklesIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Generate JSON Schema</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Add instructions to customize the schema (optional)
              </p>
            </div>
          </div>

          <Textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g., Include email validation and required fields..."
            disabled={mutation.isPending}
            className="min-h-[80px] resize-none text-sm"
          />

          <Button isLoading={mutation.isPending} onClick={handleGenerate} size="sm">
            Generate
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { AIJSONSchemaAssistant };
